import { useMemo, useState } from "react";
import type React from "react";
import { useNavigate } from "react-router-dom";
import { getDocuments, ingestDocument, type QueueItem } from "../../../../lib/corpusApi";
import { useAsyncPaginatedData } from "../../../../hooks/useAsyncPaginatedData";
import { PAGE_SIZE_OPTIONS, type ListPagination } from "../../../../lib/pagination";
import { useDebouncedValue } from "../../../../hooks/useDebouncedValue";
import { useUrlFilters } from "../../../../lib/url_filters";
import { documentListFiltersSchema, type DocumentListFilters } from "./documentListFilters";
import { useTaxonomy } from "../../../../context/TaxonomyProvider";
import { ROUTES } from "../../../../route_paths";

interface RowState {
  loading: boolean;
  error: string | null;
  success: boolean;
}

export interface UseDocumentListResult {
  documents: QueueItem[];
  loading: boolean;
  error: string | null;
  filters: DocumentListFilters;
  setFilters: (filters: DocumentListFilters) => void;
  query: string;
  setQuery: (v: string) => void;
  rowState: Record<string, RowState>;
  handleIngest: (id: string) => void;
  handleAnnotate: (id: string) => void;
  refresh: () => void;
  pagination: ListPagination;
}

/** Fetches and paginates the corpus document table, narrowed by the filters in the URL, and drives
 *  its per-row actions (ingest, annotate). One document at a time: batching, if ever needed, is a
 *  client-side loop over the same calls. */
export function useDocumentList(): UseDocumentListResult {
  const navigate = useNavigate();
  const [urlFilters, setFilters] = useUrlFilters(documentListFiltersSchema);
  const taxonomy = useTaxonomy();
  // A link may name a field the skeleton no longer has; once the skeleton is loaded it is dropped.
  const filters = useMemo(
    () =>
      taxonomy.isEmpty
        ? urlFilters
        : { ...urlFilters, fields: urlFilters.fields.filter((urn) => taxonomy.findField(urn)) },
    [urlFilters, taxonomy]
  );
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebouncedValue(query, 300);
  const [rowState, setRowState] = useState<Record<string, RowState>>({});
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const {
    items: documents,
    loading,
    error,
    pagination: basePagination,
    refresh: fetchDocuments,
  } = useAsyncPaginatedData(
    (offsetPage) =>
      getDocuments({
        status: filters.status,
        q: debouncedQuery || undefined,
        fields: filters.fields,
        feedId: filters.feedId ?? undefined,
        subject: filters.subject ?? undefined,
        ...offsetPage,
      }),
    {
      pageSize: rowsPerPage,
      key: `${filters.status}:${filters.fields.join(",")}:${filters.feedId ?? ""}:${filters.subject ?? ""}:${debouncedQuery}`,
    }
  );
  const pagination: ListPagination = {
    ...basePagination,
    pageSizeOptions: PAGE_SIZE_OPTIONS,
    onPageSizeChange: setRowsPerPage,
  };

  function setRowLoading(
    id: string,
    loading: boolean,
    error: string | null = null,
    success = false
  ) {
    setRowState((prev) => ({ ...prev, [id]: { loading, error, success } }));
  }

  async function handleIngest(id: string) {
    setRowLoading(id, true);
    try {
      await ingestDocument(id);
      setRowLoading(id, false, null, true);
      void fetchDocuments();
    } catch (err) {
      setRowLoading(id, false, err instanceof Error ? err.message : "Failed");
    }
  }

  function handleAnnotate(id: string) {
    navigate(`${ROUTES.internal.annotation.review.path()}?sourceId=${id}`);
  }

  return {
    documents,
    loading,
    error,
    filters,
    setFilters,
    query,
    setQuery,
    rowState,
    handleIngest,
    handleAnnotate,
    refresh: fetchDocuments,
    pagination,
  };
}
